import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import connectApi from 'api/connectApi'
import * as Sentry from '@sentry/react'
import {
    MobilePaymentPayload,
    MobilePaymentResponse,
    MobilePaymentState,
    MobilePaymentSliceEnum
} from 'storeTypes/mobilePaymentTypes'
import { constants } from 'helpers'
import { generateUuidv4 } from 'helpers/generateUUIDV4'
import { isPaymentResultForAttempt } from 'helpers/paymentAttempt'

type SuccessfulMobilePayment = MobilePaymentResponse
type FailedMobilePayment = { error: string; uuid?: string }

const { MOBILE_PAYMENT } = constants.endpoints

export const mobilePaymentAction = createAsyncThunk<
    SuccessfulMobilePayment,
    MobilePaymentPayload,
    {
        rejectValue: FailedMobilePayment
    }
>(
    MobilePaymentSliceEnum.MOBILE_PAYMENT_ACTION,
    async (paymentData: MobilePaymentPayload, thunkAPI) => {
        const uuid = generateUuidv4()
        try {
            thunkAPI.dispatch(setMobilePaymentUuid(uuid))

            const payloadWithUuid = {
                ...paymentData,
                uuid
            }

            const response = await connectApi.post(
                MOBILE_PAYMENT,
                payloadWithUuid
            )

            if (response.status >= 400 || response.status === 0) {
                return thunkAPI.rejectWithValue({
                    error: response.data?.message || 'Payment request failed',
                    uuid
                })
            }

            return { ...response.data, uuid }
        } catch (error) {
            Sentry.captureException(error)
            return thunkAPI.rejectWithValue({
                error: 'Something went wrong',
                uuid
            })
        }
    }
)

export const checkPaymentConfirmation = createAsyncThunk<
    { status: string; uuid: string },
    string,
    {
        rejectValue: FailedMobilePayment
    }
>(
    MobilePaymentSliceEnum.CHECK_PAYMENT_CONFIRMATION,
    async (uuid: string, thunkAPI) => {
        try {
            const response = await connectApi.get(
                `${MOBILE_PAYMENT}?uuid=${uuid}`
            )

            if (response.status === 200) {
                return { status: response.data, uuid }
            } else {
                return thunkAPI.rejectWithValue({
                    error:
                        response.data?.message ||
                        'Failed to check payment status',
                    uuid
                })
            }
        } catch (error) {
            Sentry.captureException(error)
            return thunkAPI.rejectWithValue({
                error: 'Something went wrong checking payment status',
                uuid
            })
        }
    }
)

const mobilePaymentInitialState: MobilePaymentState = {
    loading: false,
    response: undefined,
    error: undefined,
    uuid: undefined,
    status: undefined
}

const mobilePaymentSlice = createSlice({
    name: MobilePaymentSliceEnum.MOBILE_PAYMENT,
    initialState: mobilePaymentInitialState,
    reducers: {
        clearMobilePaymentAction: (state) => {
            state.loading = false
            state.response = undefined
            state.error = undefined
            state.uuid = undefined
            state.status = undefined
        },
        setMobilePaymentUuid: (state, action: PayloadAction<string>) => {
            state.uuid = action.payload
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(mobilePaymentAction.pending, (state) => {
                state.loading = true
                state.error = undefined
            })
            .addCase(
                mobilePaymentAction.fulfilled,
                (state, action: PayloadAction<SuccessfulMobilePayment>) => {
                    if (
                        !isPaymentResultForAttempt(
                            action.payload?.uuid,
                            state.uuid
                        )
                    ) {
                        return
                    }
                    state.loading = false
                    state.response = action.payload
                    state.error = undefined
                }
            )
            .addCase(
                mobilePaymentAction.rejected,
                (
                    state,
                    action: PayloadAction<FailedMobilePayment | undefined>
                ) => {
                    if (
                        !isPaymentResultForAttempt(
                            action.payload?.uuid,
                            state.uuid
                        )
                    ) {
                        return
                    }
                    state.loading = false
                    state.response = undefined
                    state.error =
                        action.payload?.error || 'Something went wrong'
                }
            )
            .addCase(
                checkPaymentConfirmation.fulfilled,
                (
                    state,
                    action: PayloadAction<{ status: string; uuid?: string }>
                ) => {
                    if (
                        !isPaymentResultForAttempt(
                            action.payload?.uuid,
                            state.uuid
                        )
                    ) {
                        return
                    }
                    state.status = action.payload.status
                }
            )
            .addCase(
                checkPaymentConfirmation.rejected,
                (
                    state,
                    action: PayloadAction<FailedMobilePayment | undefined>
                ) => {
                    if (
                        !isPaymentResultForAttempt(
                            action.payload?.uuid,
                            state.uuid
                        )
                    ) {
                        return
                    }
                    state.error =
                        action.payload?.error ||
                        'Failed to check payment status'
                }
            )
    }
})

export const { clearMobilePaymentAction, setMobilePaymentUuid } =
    mobilePaymentSlice.actions
export const mobilePaymentReducer = mobilePaymentSlice.reducer
